/**
 * SuggestionsList Component
 * Simplified version with clean UI
 */

import React, { useState } from 'react';
import { 
  ChevronRight, 
  UserPlus, 
  AlertCircle, 
  RefreshCw,
  ChevronDown,
  Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';
import useSuggestions from '../hooks/useSuggestions';
import { toast } from 'react-hot-toast';

/**
 * SuggestionsList Component
 * 
 * @param {Object} props Component props
 * @param {number} props.limit Maximum number of suggestions to show (default: 3)
 * @param {boolean} props.showViewAll Whether to show "View All" link (default: true)
 * @param {boolean} props.showHeader Whether to show the component header (default: true)
 * @param {string} props.title Custom title for the component
 * @param {Object} props.filters Optional filters for suggestions
 * @param {Function} props.onProfileView Callback when a profile is viewed
 * @returns {JSX.Element} SuggestionsList component
 */
const SuggestionsList = ({ 
  limit = 3, 
  showViewAll = true, 
  showHeader = true,
  title = "Suggested Linkers",
  filters = {},
  onProfileView
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Use custom hook to get suggestions data
  const { 
    suggestions, 
    loading, 
    error, 
    connectionStates, 
    hasRandomSuggestions,
    refreshSuggestions, 
    sendConnectionRequest
  } = useSuggestions(limit, filters);

  // Handle connect button click
  const handleConnect = async (suggestionId) => {
    try {
      await sendConnectionRequest(suggestionId, "I'd like to connect with you on VarsityLink!");
      toast.success("Connection request sent!");
      return true;
    } catch (error) {
      console.error('Error connecting with user:', error);
      toast.error("Couldn't send connection request");
      throw error;
    }
  };

  // Handle view profile
  const handleViewProfile = (suggestion) => {
    if (onProfileView) {
      onProfileView(suggestion);
    } else {
      // If no external handler, we could implement a simple modal view here
      toast.success(`Viewing ${suggestion.basicInfo?.name}'s profile`);
    }
  };

  // Handle refresh suggestions with animation
  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      await refreshSuggestions();
      toast.success('Suggestions refreshed');
    } catch (error) {
      toast.error('Failed to refresh suggestions');
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 600); // Add slight delay for animation
    }
  };

  // Render a suggestion card
  const renderSuggestionCard = (suggestion) => {
    const connectionState = connectionStates[suggestion.id] || 'none';
    
    return (
      <div key={suggestion.id} className="bg-white rounded-lg border border-gray-100 overflow-hidden animate-fadeIn mb-3">
        <div className="p-4">
          <div className="flex items-start">
            {/* Avatar */}
            <div className="w-12 h-12 rounded-full overflow-hidden mr-3 flex-shrink-0">
              {suggestion.photos && suggestion.photos.length > 0 ? (
                <img 
                  src={suggestion.photos[0]} 
                  alt={suggestion.basicInfo?.name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-green-100 flex items-center justify-center text-green-500 font-semibold">
                  {suggestion.basicInfo?.name?.charAt(0) || 'U'}
                </div>
              )}
            </div>
            
            {/* User Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-800 truncate">
                {suggestion.basicInfo?.name || 'User'}
              </h3>
              <p className="text-sm text-gray-500 truncate mb-1">
                @{suggestion.basicInfo?.username || 'username'}
              </p>
              
              {/* University & Degree */}
              {suggestion.academic?.university && (
                <p className="text-xs text-gray-600 flex items-center truncate">
                  {suggestion.academic?.university}
                  {suggestion.academic?.degree && (
                    <span className="mx-1">•</span>
                  )}
                  {suggestion.academic?.degree}
                </p>
              )}
            </div>
          </div>
          
          {/* Interests */}
          {suggestion.bio?.interests && suggestion.bio.interests.length > 0 && (
            <div className="mt-3">
              <div className="flex flex-wrap gap-1">
                {suggestion.bio.interests.slice(0, 3).map((interest, idx) => (
                  <span 
                    key={idx}
                    className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full"
                  >
                    {interest}
                  </span>
                ))}
                {suggestion.bio.interests.length > 3 && (
                  <span className="text-xs text-gray-500">
                    +{suggestion.bio.interests.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Connection Match */}
          {suggestion.matchPercentage && (
            <div className="mt-3 flex items-center">
              <div className="h-1.5 bg-gray-200 rounded-full flex-1 mr-2">
                <div 
                  className="h-full bg-green-500 rounded-full" 
                  style={{ width: `${suggestion.matchPercentage}%` }}
                ></div>
              </div>
              <span className="text-xs font-medium text-green-600">
                {suggestion.matchPercentage}% Match
              </span>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="mt-3 flex items-center space-x-2">
            <button
              onClick={() => handleViewProfile(suggestion)}
              className="flex-1 py-1.5 px-3 border border-gray-200 rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 flex items-center justify-center"
            >
              <Eye size={16} className="mr-1" /> View
            </button>
            
            {connectionState === 'none' ? (
              <button
                onClick={() => handleConnect(suggestion.id)}
                className="flex-1 py-1.5 px-3 bg-green-500 rounded-md text-sm text-white font-medium hover:bg-green-600 flex items-center justify-center"
              >
                <UserPlus size={16} className="mr-1" /> Connect
              </button>
            ) : connectionState === 'pending' ? (
              <button
                disabled
                className="flex-1 py-1.5 px-3 bg-gray-100 rounded-md text-sm text-gray-500 font-medium flex items-center justify-center"
              >
                Pending
              </button>
            ) : (
              <button
                disabled
                className="flex-1 py-1.5 px-3 bg-gray-100 rounded-md text-sm text-gray-500 font-medium flex items-center justify-center"
              >
                Connected
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow-sm rounded-lg mb-4 overflow-hidden">
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              {title}
              {hasRandomSuggestions && (
                <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  Random
                </span>
              )}
            </h2>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={loading || isRefreshing}
              className={`text-gray-400 hover:text-gray-600 p-1 transition-transform ${isRefreshing ? 'animate-spin' : ''}`}
              aria-label="Refresh suggestions"
            >
              <RefreshCw size={16} />
            </button>
            
            {showViewAll && (
              <Link to="/connect?tab=discover" className="text-sm text-green-500 flex items-center">
                View All <ChevronRight size={16} />
              </Link>
            )}
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="p-4">
        {loading && suggestions.length === 0 ? (
          <div className="py-8 text-center">
            <div className="w-10 h-10 mx-auto mb-3 relative">
              <div className="absolute inset-0 border-2 border-t-transparent border-green-500 rounded-full animate-spin"></div>
              <div className="absolute inset-3 bg-white rounded-full flex items-center justify-center">
                <UserPlus size={14} className="text-green-500" />
              </div>
            </div>
            <p className="text-gray-500 text-sm">Finding people you might know...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-red-50 rounded-full mx-auto mb-3 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <p className="text-red-500 font-medium mb-2 text-sm">Something went wrong</p>
            <p className="text-gray-500 text-sm mb-4">{error}</p>
            <button 
              onClick={refreshSuggestions}
              className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : suggestions.length > 0 ? (
          <div className="space-y-4">
            {suggestions.map(suggestion => renderSuggestionCard(suggestion))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <UserPlus className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-gray-800 font-medium mb-1">No suggestions found</h3>
            <p className="text-gray-500 text-sm mb-4">
              Complete your profile to get better suggestions
            </p>
            <Link 
              to="/connect"
              className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm inline-flex items-center hover:bg-green-600 transition-colors"
            >
              <UserPlus size={16} className="mr-2" /> Find More Connections
            </Link>
          </div>
        )}
      </div>
      
      {/* "Show more" link for mobile when there are more suggestions */}
      {!showViewAll && suggestions.length > 0 && suggestions.length === limit && (
        <div className="p-3 text-center border-t border-gray-100">
          <Link 
            to="/connect?tab=discover"
            className="text-green-500 text-sm font-medium hover:text-green-600 flex items-center justify-center w-full"
          >
            Show more <ChevronDown size={16} className="ml-1" />
          </Link>
        </div>
      )}
      
      {/* Add animations */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default SuggestionsList;